// cspell:words kontroller

import fuzzysort from 'fuzzysort'

import CustomHTMLElement from '@onpace/onspace-core/components/html_element'
import client from '@onpace/onspace-core/components/client'
import { isScrollingRetained } from '@onpace/onspace-core/components/scrollretain'
import translation from '@onpace/onspace-core/components/translations'

import OnspaceDialog from '@onpace/onspace-core/elements/dialog'

const ACTIONS_INDEX_PATH = '/onspace/cms/actions'
const ACTIONS_SEARCH_PATH = '/onspace/cms/actions/search'

const MAX_RECENT_ITEMS = 5
const PINNED_ITEMS_KEY = 'onspace:cms:actions:pinned-items'
const RECENT_ITEMS_KEY = 'onspace:cms:actions:recent-items'

////////// Element

export class OnspaceActionsMenu extends CustomHTMLElement {
  /// Sets up the actions menu element.
  ///
  /// This creates and appends a html +dialog+ element, and registers event listeners as necessary.
  runFirstConnected() {
    super.runFirstConnected()

    this.dialogElement = document.createElement('dialog')
    this.dialogElement.addEventListener('cancel', this.onCancel.bind(this))
    this.dialogElement.addEventListener('click', this.dialogClicked.bind(this), { capture: true })
    this.appendChild(this.dialogElement)

    this.contentElement = document.createElement('div')
    this.contentElement.classList.add('onspace-actions__content')
    this.contentElement.addEventListener('mousemove', this.contentMouseMoved.bind(this))
    this.dialogElement.appendChild(this.contentElement)

    this.prepareItemsData()

    this.setupInputContent()
    this.setupMessageContent()
    this.setupResultContent()

    document.addEventListener('keypress', this.documentKeyPressed.bind(this))
  }

  ////////// State

  /// Indicates if the menu is currently open.
  get open() {
    return this.dialogElement.open
  }

  /// Shows the actions menu.
  show() {
    if (this.open) { return }

    this.loadItemIndex()

    this.dialogElement.showModal()

    setTimeout(() => {
      this.inputElement.focus()
      this.inputElement.select()
    })

    this.displayPromptItems = true
    this.updateResults()

    document.body.classList.add('onspace-actions--active')
    this.addDocumentBoundEventListener('keydown', this.documentKeyDowned)
    this.triggerEvent('onspace:actions:show')
  }

  /// Hides the actions menu.
  hide() {
    if (!this.open) { return }

    const dialogAnimationEnded = (_event) => {
      this.dialogElement.removeEventListener('animationend', dialogAnimationEnded)
      this.dialogElement.removeAttribute('close')
      this.dialogElement.close()

      document.body.classList.remove('onspace-actions--active')
      this.triggerEvent('onspace:actions:hide')
    }
    this.dialogElement.addEventListener('animationend', dialogAnimationEnded)

    this.removeDocumentBoundEventListener('keydown')
    this.inputElement.blur()
    this.dialogElement.setAttribute('close', '')
  }

  /// Callback run when the dialog is cancelled by the user.
  ///
  /// The cancel event is prevented, we manually call this element's +hide+ function, so the animations still run.
  onCancel(event) {
    event.preventDefault()
    this.hide()
  }

  ///// Events

  /// Callback run when the dialog is clicked.
  ///
  /// This detects when clicks are placed on the dialog's background, and if so closes the actions menu.
  dialogClicked(event) {
    if (event.clientX == 0 && event.clientY == 0) { return }
    if (isScrollingRetained()) { return }

    const rect = this.contentElement.getBoundingClientRect()
    if (event.clientY < rect.top || rect.bottom < event.clientY || event.clientX < rect.left || rect.right < event.clientX) {
      event.preventDefault()
      event.stopPropagation()
      this.hide()
    }
  }

  /// Responds to keyboard presses on the document.
  documentKeyPressed(event) {
    if (this.open && event.target !== this.inputElement) {
      this.inputElement.focus()
      return
    }

    if (event.target !== document.body) { return }

    switch (event.key) {
    case '/':
      this.show()
      break
    default:
      return
    }

    event.preventDefault()
  }

  /// Responds to keydowns on the document.
  ///
  /// Note that this will only be triggered when the menu is visible.
  documentKeyDowned(event) {
    return this.inputKeyPressed(event)
  }

  /// Callback run when hovering over the content element.
  contentMouseMoved(_event) {
    this.clearHighlightedResultedItem()
  }

  ////////// Input

  /// Sets up the input components and events.
  setupInputContent() {
    const inputContainer = document.createElement('div')
    inputContainer.classList.add('onspace-actions__input')
    this.contentElement.appendChild(inputContainer)

    const iconElement = SVGElement.createOnspaceSpritemapSvg('onspace/icon_search')
    inputContainer.appendChild(iconElement)

    this.expandedItemsElement = document.createElement('div')
    this.expandedItemsElement.classList.add('onspace-actions__input__expanded')
    inputContainer.appendChild(this.expandedItemsElement)

    this.inputElement = document.createElement('input')
    this.inputElement.placeholder = translation('onspace.cms.actions.input.placeholder')
    this.inputElement.autocapitalize = 'off'
    this.inputElement.autocomplete = 'off'
    this.inputElement.autocorrect = false
    this.inputElement.spellcheck = false
    this.inputElement.addEventListener('input', this.inputElementUpdated.bind(this))
    this.inputElement.addEventListener('keypress', this.inputKeyPressed.bind(this))
    inputContainer.appendChild(this.inputElement)

    this.loadingIcon = SVGElement.createOnspaceSpritemapSvg('onspace/logo_onspace_loading')
    this.loadingIcon.classList.add('onspace-actions__input__loading')
    inputContainer.appendChild(this.loadingIcon)
  }

  /// Clears the current search query.
  clearInputValue() {
    this.inputElement.value = ''
    this.clearExpandedItems()
    this.updateResults()
  }

  /// Creates an expanded item element from the given item.
  ///
  /// The item will be automatically added to the DOM.
  createExpandedItem(item) {
    const itemElement = document.createElement('div')
    itemElement.classList.add('onspace-actions__input__expanded__item')

    ///// Remove

    const removeButton = document.createElement('a')
    removeButton.classList.add('onspace-actions__input__expanded__item__remove')
    removeButton.addEventListener('click', this.expandedItemRemoveClicked.bind(this))
    itemElement.appendChild(removeButton)

    const removeIcon = SVGElement.createOnspaceSpritemapSvg('onspace/icon_cross')
    removeButton.appendChild(removeIcon)

    ///// Title

    const titleElement = document.createElement('span')
    titleElement.innerText = item.title
    itemElement.appendChild(titleElement)

    this.expandedItemsElement.appendChild(itemElement)
  }

  ///// Events

  /// Callback run when the input element changes its value.
  inputElementUpdated(_event) {
    this.displayPromptItems = false

    this.updateResults()
    this.loadSearchItems()
  }

  /// Responds to key presses on the input.
  inputKeyPressed(event) {
    switch (event.key) {
    case 'ArrowUp':
      this.highlightPreviousResultItem()
      event.preventDefault()
      event.stopPropagation()
      break
    case 'ArrowDown':
      this.highlightNextResultItem()
      event.preventDefault()
      event.stopPropagation()
      break
    case 'Enter':
      this.resultItemEntered(event)
      break
    case 'Tab':
      this.resultItemTabbed(event)
      break
    case 'π':
      this.resultItemOptionPd(event)
      break
    case 'Backspace':
      this.resultItemBackspaced(event)
      break
    }

    if (49 <= event.keyCode && event.keyCode <= 57 && event.altKey) {
      this.pinnedItemTriggered(event, event.keyCode - 48)
    }
  }

  /// Responds to clicks on an expanded item's remove button.
  ///
  /// This determines the relevant item and removes it and any items higher than it.
  expandedItemRemoveClicked(event) {
    let itemElement = event.target
    itemElement = itemElement.closest('.onspace-actions__input__expanded__item')

    this.popExpandedItem(itemElement)
  }

  ////////// Message

  /// Sets up the message components and events.
  setupMessageContent() {
    this.messageElement = document.createElement('div')
    this.messageElement.classList.add('onspace-actions__message')
    this.contentElement.appendChild(this.messageElement)
  }

  /// Displays a message above the results element.
  showMessage(message, options = {}) {
    if (this.responseErrorMessage) { return }

    this.clearMessage()

    this.messageElement.innerText = message

    if (options.type) {
      this.messageElement.classList.add(`onspace-actions__message--${options.type}`)
      this.messageElement.messageType = options.type
    }
  }

  /// Clears any existing results message.
  clearMessage() {
    if (this.responseErrorMessage) { return }

    this.messageElement.innerText = ''

    if (this.messageElement.messageType) {
      this.messageElement.classList.remove(`onspace-actions__message--${this.messageElement.messageType}`)
      this.messageElement.messageType = null
    }
  }

  /// Displays an error messages which has resulted from a failed request.
  async showResponseError(response) {
    this.clearResponseError()

    let message = null
    try {
      const content = await response.content()
      if (typeof content.error === 'object' && typeof content.error.text === 'string') {
        message = content.error.text
      }
    } catch (_error) {
      // ignore
    }

    if (typeof message !== 'string' || message.length === 0) { message = translation('onspace.cms.actions.error.unknown') }

    this.showMessage(message, { type: 'error' })
    this.responseErrorMessage = message
  }

  /// Clears an existing response error message.
  clearResponseError() {
    if (!this.responseErrorMessage) { return }

    this.responseErrorMessage = null
    this.clearMessage()
  }

  ////////// Results

  /// Loads recent items from local storage.
  prepareItemsData() {
    this.expandedItems = []
    this.pinnedItems = []
    this.recentItems = []

    const pinnedItemsStorage = window.localStorage.getItem(PINNED_ITEMS_KEY)
    if (typeof pinnedItemsStorage === 'string' && pinnedItemsStorage.length > 0) {
      this.pinnedItems = JSON.parse(pinnedItemsStorage)
    }

    const recentItemsStorage = window.localStorage.getItem(RECENT_ITEMS_KEY)
    if (typeof recentItemsStorage === 'string' && recentItemsStorage.length > 0) {
      this.recentItems = JSON.parse(recentItemsStorage)
    }
  }

  /// Loads the item index from the server.
  ///
  /// This requests the item index and stores it in memory for as long as the page is left open.
  async loadItemIndex() {
    if (this.indexLoaded) { return }

    this.indexLoaded = true
    this.isLoading = true
    this.clearResponseError()

    const response = await client.get(ACTIONS_INDEX_PATH)

    if (response.isSuccess) {
      const content = await response.content()

      this.itemIndex = content.items

      this.updateResults()
    } else {
      await this.showResponseError(response)
      this.itemIndex = []
    }

    this.isLoading = false
  }

  /// Loads expanded items for the given search query.
  ///
  /// This requests the items and stores them in memory.
  async loadSearchItems() {
    if (this.expandedItems.length === 0) { return }

    const lastExpandedItem = this.expandedItems[this.expandedItems.length - 1]
    if (typeof lastExpandedItem.searchable !== 'string' || lastExpandedItem.searchable.length === 0) { return }

    const query = this.inputElement.value
    if (query.length === 0) {
      this.clearSearchItems()
      return
    } else if (this.isLoading) {
      this.nextSearchQuery = query
      return
    }

    this.nextSearchQuery = null
    this.isLoading = true
    this.clearResponseError()

    const response = await client.get(ACTIONS_SEARCH_PATH, { params: { kontroller: lastExpandedItem.searchable, query } })

    if (response.isSuccess) {
      const content = await response.content()
      this.searchItemIndex = content.items
      this.searchItemIndex.forEach((item) => {
        if (item.icon === '_parent') { item.icon = lastExpandedItem.icon }

        if (item.children) {
          item.children.forEach((child) => {
            if (child.icon === '_parent') { child.icon = item.icon }
          })
        }
      })
    } else {
      await this.showResponseError(response)
      this.searchItemIndex = null
    }

    this.isLoading = false

    if (this.nextSearchQuery !== null && this.nextSearchQuery !== query) {
      await this.loadSearchItems()
    }

    this.updateResults()
  }

  // Clears the current search query.
  clearSearchItems() {
    this.nextSearchQuery = null
    this.searchItemIndex = null

    this.clearResponseError()
  }

  /// Determines whether the given item is pinned.
  isItemPinned(item) {
    return this.pinnedItems.some((pinnedItem) => pinnedItem.url === item.url)
  }

  togglePinnedItem(item) {
    const currentIndex = this.pinnedItems.findIndex((pinnedItem) => pinnedItem.url === item.url)

    if (currentIndex === -1) {
      const storageItem = this.prepareStorageItem(item)
      this.pinnedItems.push(storageItem)
    } else {
      this.pinnedItems.splice(currentIndex, 1)
    }

    this.updateResults()

    const storageItems = JSON.stringify(this.pinnedItems)
    window.localStorage.setItem(PINNED_ITEMS_KEY, storageItems)
  }

  /// Adds an item to the list of recently used.
  ///
  /// This will add the item to the top of the list, and remove one from the bottom if it exceeds the limit. If an item
  /// with the same URL already exists in the list, that will be removed. The items will be persisted to local storage.
  addRecentItem(item) {
    const storageItem = this.prepareStorageItem(item)

    this.recentItems = this.recentItems.filter((recentItem) => recentItem.url !== storageItem.url)
    this.recentItems.unshift(storageItem)
    while (this.recentItems.length > MAX_RECENT_ITEMS) {
      this.recentItems.pop()
    }

    const storageItems = JSON.stringify(this.recentItems)
    window.localStorage.setItem(RECENT_ITEMS_KEY, storageItems)
  }

  /// Prepares an item which will be stored in the browser.
  prepareStorageItem(item) {
    let storageTitle = item.title
    let storageIcon = item.icon
    if (this.expandedItems.length > 0) {
      this.expandedItems.slice().reverse().forEach((expandedItem) => {
        storageTitle = `${expandedItem.title} - ${storageTitle}`
        if (!storageIcon && expandedItem.icon) { storageIcon = expandedItem.icon }
      })
    }

    return {
      title: storageTitle,
      icon: storageIcon,
      color: item.color,
      url: item.url,
      dialog: item.dialog
    }
  }

  /// Determines which items should be available for the given text.
  calculateAvailableItems(text) {
    let items = []
    let message = null

    if (this.expandedItems.length > 0) {
      const lastExpandedItem = this.expandedItems[this.expandedItems.length - 1]
      items = lastExpandedItem.children || []

      if (text.length === 0) {
        items = items.map((item) => this.prepareResultedItem(item))
      } else {
        if (this.searchItemIndex) {
          const fullResultsItem = this.searchItemIndex.find((item) => item.icon === 'onspace/icon_search' && item.title !== undefined)

          let searchItems = this.searchItemIndex
          if (fullResultsItem) {
            searchItems = searchItems.filter((item) => item !== fullResultsItem)
          }

          items = searchItems.concat(items)

          items = fuzzysort
            .go(text, items, { key: 'title' })
            .map((result) => this.prepareResultedItem(result.obj, { result }))

          if (fullResultsItem) {
            items.push(this.prepareResultedItem(fullResultsItem))
          }
        } else {
          items = fuzzysort
            .go(text, items, { key: 'title' })
            .map((result) => this.prepareResultedItem(result.obj, { result }))
        }

        if (items.length === 0) {
          if (this.isLoading) {
            message = translation('onspace.cms.actions.results.loading')
          } else {
            message = translation('onspace.cms.actions.results.none')
          }
        }
      }
    } else if (text.length === 0 || this.displayPromptItems) {
      items = this.pinnedItems.map((item, index) => this.prepareResultedItem(item, { index: index + 1 }))
      this.recentItems.forEach((item) => {
        const preparedItem = this.prepareResultedItem(item)
        if (!preparedItem.pinned) { items.push(preparedItem) }
      })

      if (items.length > 0) { message = translation('onspace.cms.actions.results.recent') }
    } else if (this.indexLoaded && this.itemIndex) {
      items = fuzzysort
        .go(text, this.itemIndex, { key: 'title' })
        .map((result) => this.prepareResultedItem(result.obj, { result }))

      if (items.length === 0) { message = translation('onspace.cms.actions.results.none') }
    }

    return [items, message]
  }

  /// Prepares a result item to be ready for UI.
  ///
  /// This works with fuzzy sort results and plain items, ensuring they have a uniform output.
  prepareResultedItem(item, { result, index } = {}) {
    let text = item.title
    const pinned = this.isItemPinned(item)
    if (typeof result === 'object') {
      text = result.highlight('<b>', '</b>')
    } else if (index) {
      text = `${index}. ${text}`
    }

    return {
      text,
      pinned,
      item
    }
  }

  ///// Elements

  /// Sets up the result components and events.
  setupResultContent() {
    this.resultsElement = document.createElement('div')
    this.resultsElement.classList.add('onspace-actions__results')
    this.contentElement.appendChild(this.resultsElement)

    this.highlightedResultItem = null
  }

  /// Creates an result element from the given item.
  ///
  /// The text will be highlighted in the item's title.
  createResultItem(result) {
    const itemElement = document.createElement('a')
    itemElement.item = result.item
    itemElement.classList.add('onspace-actions__results__item')
    itemElement.addEventListener('click', this.resultItemClicked.bind(this))

    if (result.item.color) {
      itemElement.classList.add(`onspace-actions__results__item--${result.item.color}`)
    }

    const iconElement = SVGElement.createOnspaceSpritemapSvg(result.item.icon)
    itemElement.appendChild(iconElement)

    const titleElement = document.createElement('span')
    titleElement.innerHTML = result.text
    itemElement.appendChild(titleElement)

    if (result.item.searchable || (result.item.children && result.item.children.length > 0)) {
      const moreElement = document.createElement('div')
      moreElement.classList.add('onspace-actions__results__item__more')
      moreElement.addEventListener('click', this.resultMoreClicked.bind(this))
      itemElement.appendChild(moreElement)

      const iconName = result.item.searchable ? 'onspace/icon_search' : 'onspace/icon_ellipsis'
      const moreIcon = SVGElement.createOnspaceSpritemapSvg(iconName)
      moreElement.appendChild(moreIcon)
    }

    const pinElement = document.createElement('div')
    pinElement.classList.add('onspace-actions__results__item__pin')
    pinElement.addEventListener('click', this.resultPinClicked.bind(this))
    itemElement.appendChild(pinElement)

    const iconName = result.pinned ? 'onspace/icon_pin' : 'onspace/icon_pin--outline'
    const pinIcon = SVGElement.createOnspaceSpritemapSvg(iconName)
    pinElement.appendChild(pinIcon)

    return itemElement
  }

  /// Indicates whether the element is currently loading.
  get isLoading() {
    return this.loadingIcon.style.display !== 'none'
  }

  /// Updates the element's loading state.
  set isLoading(newValue) {
    this.loadingIcon.style.display = newValue ? '' : 'none'
  }

  /// Updates the results based on the current search query.
  updateResults() {
    this.clearMessage()
    this.clearHighlightedResultedItem()
    this.resultsElement.innerHTML = ''

    const text = this.inputElement.value
    const [results, message] = this.calculateAvailableItems(text)
    results.forEach((result) => {
      const itemElement = this.createResultItem(result)
      this.resultsElement.appendChild(itemElement)
    })

    if (typeof message === 'string') { this.showMessage(message) }
    this.highlightNextResultItem()
  }

  ///// Events

  /// Callback run when an item is clicked.
  ///
  /// This determines the relevant item and selects it.
  resultItemClicked(event) {
    let itemElement = event.target
    if (itemElement.tagName !== 'A') { itemElement = itemElement.closest('a') }

    const newWindow = event.metaKey || event.ctrlKey
    this.selectItem(itemElement.item, { newWindow })
  }

  /// Callback run when the more indicator of an item is clicked.
  ///
  /// This determines the relevant item and expands it.
  resultMoreClicked(event) {
    event.stopPropagation()

    let itemElement = event.target
    if (itemElement.tagName !== 'A') { itemElement = itemElement.closest('a') }

    this.expandItem(itemElement.item)
  }

  /// Callback run when the pin indicator of an item is clicked.
  ///
  /// This toggles the item's pinned state.
  resultPinClicked(event) {
    event.stopPropagation()

    let itemElement = event.target
    if (itemElement.tagName !== 'A') { itemElement = itemElement.closest('a') }

    this.togglePinnedItem(itemElement.item)
  }

  /// Callback run when the element is entered.
  ///
  /// This determines the highlighted item and selects it.
  resultItemEntered(event) {
    event.preventDefault()
    event.stopPropagation()

    const itemElement = this.highlightedResultItem
    if (!itemElement) { return }

    const newWindow = event.metaKey || event.ctrlKey
    this.selectItem(itemElement.item, { newWindow })
  }

  /// Callback run when the element is tabbed.
  ///
  /// This determines the highlighted item and expands it.
  resultItemTabbed(event) {
    event.preventDefault()
    event.stopPropagation()

    const itemElement = this.highlightedResultItem
    if (!itemElement) { return }

    this.expandItem(itemElement.item)
  }

  /// Callback run when option-p is triggered on the element.
  ///
  /// This determines the highlighted item and toggles its pin.
  resultItemOptionPd(event) {
    event.preventDefault()
    event.stopPropagation()

    const itemElement = this.highlightedResultItem
    if (!itemElement) { return }

    this.togglePinnedItem(itemElement.item)
    this.highlightResultItem(itemElement.item)
  }

  /// Calback run when the element is backspaced.
  ///
  /// If the input cursor is at the beginning, it will remove the top expanded item. If the meta-key is held, it will
  /// clear text and all expanded items.
  resultItemBackspaced(event) {
    if (this.inputElement.selectionStart !== 0 || this.inputElement.selectionEnd !== 0) { return }

    event.preventDefault()
    event.stopPropagation()

    const metaKey = event.metaKey || event.ctrlKey
    if (metaKey) {
      this.clearExpandedItems()
    } else {
      this.popExpandedItem()
    }
  }

  /// Callback run when a pinned item is triggered.
  ///
  /// This determines the item and selects it.
  pinnedItemTriggered(event, index) {
    event.preventDefault()
    event.stopPropagation()

    const item = this.pinnedItems[index - 1]
    if (!item) { return }

    this.selectItem(item)
  }

  ///// Highlighting

  /// Highlights the previous item in the result.
  ///
  /// If there is no item highlighted, it highlights the last item.
  highlightPreviousResultItem() {
    let previousItem
    if (this.highlightedResultItem === null) {
      previousItem = this.resultsElement.lastChild
    } else {
      previousItem = this.highlightedResultItem.previousSibling
    }

    if (!previousItem) { return }

    this.clearHighlightedResultedItem()

    previousItem.setAttribute('highlighted', '')
    this.highlightedResultItem = previousItem
  }

  /// Highlights the next item in the results.
  ///
  /// If there is no item highlighted, it highlights the first item.
  highlightNextResultItem() {
    let nextItem
    if (this.highlightedResultItem === null) {
      nextItem = this.resultsElement.firstChild
    } else {
      nextItem = this.highlightedResultItem.nextSibling
    }

    if (!nextItem) { return }

    this.clearHighlightedResultedItem()

    nextItem.setAttribute('highlighted', '')
    this.highlightedResultItem = nextItem
  }

  /// Highlights the given item in the results.
  ///
  /// This will attempt to locate a result element matching the given item, and highlight it. If it cannot be found,
  /// this does nothing.
  highlightResultItem(item) {
    const resultElement = Array.from(this.resultsElement.children).find((element) => element.item.url === item.url)

    if (resultElement) {
      this.clearHighlightedResultedItem()

      resultElement.setAttribute('highlighted', '')
      this.highlightedResultItem = resultElement
    }
  }

  /// Clears the highlighted element.
  clearHighlightedResultedItem() {
    if (this.highlightedResultItem === null) { return }

    this.highlightedResultItem.removeAttribute('highlighted')
    this.highlightedResultItem = null
  }

  ///// Selection

  /// Runs the action associated with the result item.
  ///
  /// The action performed differs depending on the actual item, however the following are supported:
  /// - Navigating to the item's link.
  /// - Opening the item's link in a dialog.
  ///
  /// This accepts the following parameters:
  /// [item]
  ///   The selected item.
  /// [:newWindow]
  ///   When +true+, any links will be opened in a new window.
  selectItem(item, options = {}) {
    const newWindow = options.newWindow === true

    if (newWindow) {
      window.open(item.url, '_blank')
    } else if (item.dialog) {
      const dialog = new OnspaceDialog()
      dialog.loadUrl(item.url)
      document.body.appendChild(dialog)
    } else {
      window.Turbo.visit(item.url)
    }

    this.addRecentItem(item)

    this.hide()
    this.clearInputValue()
  }

  /// Changes the current context to the item's children.
  expandItem(item) {
    if (!item.children && !item.searchable) { return }

    this.expandedItems.push(item)
    this.createExpandedItem(item)

    this.inputElement.value = ''
    this.clearSearchItems()
    this.updateResults()
  }

  /// Removes the last expanded item.
  ///
  /// If an +element+ argument is given, it will remove that expanded item along with all of the items after it.
  popExpandedItem(element) {
    if (this.expandedItems.length === 0) { return }

    if (typeof element !== 'undefined') {
      if (!element.classList.contains('onspace-actions__input__expanded__item')) {
        throw 'Invalid expanded item element'
      }

      while (element.isConnected) {
        this.expandedItems.pop()
        this.expandedItemsElement.lastChild.remove()
      }
    } else {
      this.expandedItems.pop()
      this.expandedItemsElement.lastChild.remove()
    }

    this.displayPromptItems = false
    this.clearSearchItems()
    this.updateResults()
  }

  /// Removes all expanded items.
  clearExpandedItems() {
    this.expandedItems = []
    this.expandedItemsElement.innerHTML = ''

    this.displayPromptItems = false
    this.clearSearchItems()
    this.updateResults()
  }

  ////////// Callbacks

  /// Callback for when a page is loaded.
  ///
  /// This appends itself to the body, and searches for any buttons.
  pageDidLoad() {
    document.body.appendChild(this)

    document.querySelectorAll('[data-onspace-cms-actions]').forEach((button) => {
      button.addEventListener('click', this.show.bind(this))
    })
  }
}

window.customElements.define('onspace-actions', OnspaceActionsMenu)

////////// Global

const menu = new OnspaceActionsMenu()
export default menu

document.addEventListener('turbo:load', (_event) => {
  menu.pageDidLoad()
})
